import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Freeze} from 'react-freeze';
import {ActivityIndicator, Platform, StyleSheet, View} from 'react-native';
import Animated from 'react-native-reanimated';
import {useFocusEffect, useIsFocused} from '@react-navigation/native';
import {ThemeProvider} from '@rneui/themed';
import {FlashList, FlashListRef, ListRenderItem} from '@shopify/flash-list';
import {observer} from 'mobx-react-lite';

import ItemSeparator from '../../components/ItemSeparator';
import LoadingImg from '../../components/LoadingImg';
import {ProductTileCarousel} from '../../components/ProductTile';
import {ProductCardSkeleton} from '../../components/skeleton';
import StickyFilterPillBar, {StickyFilterDropdown} from '../../components/StickyFilterBar';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {useSearchState} from '../../context/SearchContext';
import {space} from '../../design-system/tokens';
import useStorePriceDisplayConditions from '../../hooks/usePriceDisplayConditions';
import {useScrollCollapseJSHandler} from '../../hooks/useScrollCollapse';
import {ScreenTypes, useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {useSearchLines} from '../../queries/search/useSearchLines';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import rewardsTheme from '../../theme/rewardsTheme';
import {OrderLine} from '../../transport/apiServiceResponse';
import {applyTierSort} from '../../utils/rewards';

import {useCollapsingListLayout} from './useCollapsingListLayout';

const COLUMN_GAP = 8;
const CONTENT_PADDING = 8;

export const RangeListEmpty = ({isFetching}: {isFetching: boolean}) =>
  isFetching ? (
    <LoadingImg />
  ) : (
    <View style={emptyStyles.container}>
      <Text style={emptyStyles.text}>NO PRODUCTS FOUND</Text>
    </View>
  );

const emptyStyles = StyleSheet.create({
  container: {justifyContent: 'flex-end', alignItems: 'center', height: 200},
  text: {fontSize: 16, fontWeight: '500', color: theme.colour.text.heading.primary},
});

// Grid of tile bones laid out like the FlashList below, so loaded tiles land in place.
export const RangeListSkeleton = ({numColumns}: {numColumns: number}) => (
  <View
    style={skeletonStyles.grid}
    accessibilityRole="progressbar"
    accessibilityLabel="Loading products"
    accessibilityState={{busy: true}}
    testID="range-product-skeleton">
    {Array.from({length: numColumns * 3}, (_, i) => (
      <View key={i} style={[skeletonStyles.cell, {width: `${100 / numColumns}%`}]}>
        <ProductCardSkeleton fullWidth />
      </View>
    ))}
  </View>
);

const skeletonStyles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: space.s8,
    paddingHorizontal: COLUMN_GAP / 2,
  },
  cell: {paddingHorizontal: COLUMN_GAP / 2},
});

const FOOTER_STYLE = StyleSheet.create({footer: {marginTop: 24}});
export const RangeListFooter = ({isFetchingNextPage}: {isFetchingNextPage: boolean}) =>
  isFetchingNextPage ? (
    <View style={FOOTER_STYLE.footer}>
      <ActivityIndicator size="large" color={theme.colour.activityIndicator.primary} />
    </View>
  ) : null;

const RangeProductListScreen = observer(() => {
  const {globalStore} = useStores();
  const searchState = useSearchState();
  // DIS-2053: the private member_price set only for an entitled shopper, as JoinedDepartmentsScreen.
  const {showMemberPrice} = useStorePriceDisplayConditions();
  const searchQuery = useSearchLines({includePrivate: showMemberPrice.privatePricing});
  // DIS-2028: web keeps a covered stack screen mounted, and its list can still reach the end.
  const isFocused = useIsFocused();
  // DIS-2032: web hides a covered stack screen with display:none, so its list is 0 x 0, and
  // FlashList then renders every loaded row. Freeze stops the list rendering while it is hidden.
  // Web only: DIS-2032 measured the growth on web; native keeps its current behaviour.
  const freezeList = Platform.OS === 'web' && !isFocused;

  // Close dropdown when navigating away
  useFocusEffect(
    React.useCallback(() => {
      return () => globalStore.setFiltersVisible(false);
    }, [globalStore]),
  );
  const listRef = useRef<FlashListRef<OrderLine>>(null);
  const {isWebDesktopScreen, screenType} = useWebScreenDimensions();
  const {scrollHandler} = useScrollCollapseJSHandler(listRef);
  const {
    listWrapperStyle,
    onListWrapperLayout,
    wrapperStaticStyle,
    wrapperAnimatedStyle,
    spacerStyle,
    maxCollapse,
    resetScrollCollapse,
  } = useCollapsingListLayout();

  // DIS-1127 short-content fix: see `maxCollapse` in useCollapsingListLayout.
  const flashListContentStyle = useMemo(
    () => ({paddingHorizontal: COLUMN_GAP / 2, paddingBottom: maxCollapse}),
    [maxCollapse],
  );

  // Scroll to top and uncollapse header when filter, sort, search or category changes.
  // The search TanStack query cache stays intact — only the scroll position resets.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    listRef.current?.scrollToOffset({offset: 0, animated: false});
    resetScrollCollapse();
  }, [
    searchState.sortOptionId,
    searchState.activeFacets,
    searchState.specials,
    searchState.priceRange,
    searchState.query,
    searchState.departmentId,
    resetScrollCollapse,
  ]);

  // DIS-1179: tier-sort applied only when default ('top_products') sort is
  // active; explicit user sort (price/name/etc.) takes precedence.
  // DIS-2053: a specials list is already in its final order. A tier sort would move tiles as each
  // page loads.
  const {lines} = searchQuery;
  const displayLines = useMemo(
    () =>
      !searchState.specials && searchState.sortOptionId === 'top_products'
        ? applyTierSort(lines)
        : lines,
    [lines, searchState.specials, searchState.sortOptionId],
  );

  let numColumns = 2;

  switch (screenType) {
    case ScreenTypes.mobile:
      numColumns = 2;
      break;
    case ScreenTypes.desktop:
      numColumns = 4;
      break;
    case ScreenTypes.desktopLarge:
      numColumns = 6;
      break;
  }

  // No longer delay the entry based on index - as we're paginating the list,
  // we end up waiting ages for the new stuff to slide in.
  const renderItem = useCallback<ListRenderItem<OrderLine>>(
    ({item}) => (
      <View style={styles.tileWrapper} testID="range-product-flatlist-item-wrapper">
        <ProductTileCarousel item={item} fullWidth testContext="category" />
      </View>
    ),
    [],
  );

  return (
    <ContentView testID="shop-product-list-screen" style={styles.contentViewOverride}>
      <View style={listWrapperStyle} onLayout={onListWrapperLayout}>
        <ThemeProvider theme={rewardsTheme}>
          {/* Wrapper Animated.View: translateY follows header collapse.
              Contains spacer + pill bar + FlashList as flex siblings so
              tiles are naturally clipped inside FlashList bounds — no
              z-order conflict with the pill bar possible. */}
          <Animated.View style={[wrapperStaticStyle, wrapperAnimatedStyle]}>
            {/* Spacer reserves the visual area occupied by the
                transparent React Navigation header. Translates up with
                the wrapper, so when the header collapses the spacer
                disappears off-screen and the pill bar slides up to
                its clamped position at safeAreaTop. */}
            <View style={spacerStyle} pointerEvents="none" />
            <StickyFilterPillBar />
            {searchQuery.isLoading ? (
              <RangeListSkeleton numColumns={numColumns} />
            ) : (
              <Freeze freeze={freezeList}>
                <FlashList
                  key={`range-product-flashlist-${numColumns}`}
                  testID="range-product-flatlist"
                  data={displayLines}
                  renderItem={renderItem}
                  numColumns={numColumns}
                  estimatedItemSize={280}
                  style={[styles.list]}
                  onScroll={scrollHandler}
                  scrollEventThrottle={16}
                  showsVerticalScrollIndicator={isWebDesktopScreen}
                  showsHorizontalScrollIndicator={isWebDesktopScreen}
                  onEndReachedThreshold={isWebDesktopScreen ? 0.5 : 3}
                  onEndReached={() => {
                    if (isFocused && searchQuery.hasNextPage && !searchQuery.isFetchingNextPage) {
                      searchQuery.fetchNextPage();
                    }
                  }}
                  initialNumToRender={isWebDesktopScreen ? 25 : 15}
                  drawDistance={Platform.OS === 'web' ? 500 : 250}
                  keyExtractor={(item, index) => `${item.id}-${index}`}
                  ItemSeparatorComponent={ItemSeparator}
                  ref={listRef}
                  ListEmptyComponent={<RangeListEmpty isFetching={searchQuery.isFetching} />}
                  ListFooterComponent={
                    <RangeListFooter isFetchingNextPage={!!searchQuery.isFetchingNextPage} />
                  }
                  contentContainerStyle={flashListContentStyle}
                />
              </Freeze>
            )}
          </Animated.View>
          {/* Dropdown overlay — rendered at listWrapper level (outside
              the wrapper Animated.View) so the scrim always covers the
              full visible area and its own translateY animated style
              tracks the pill bar's visual position. */}
          <StickyFilterDropdown />
        </ThemeProvider>
      </View>
    </ContentView>
  );
});

export default RangeProductListScreen;

const styles = StyleSheet.create({
  contentViewOverride: {
    paddingHorizontal: 0,
  },
  tileWrapper: {
    flex: 1,
    paddingHorizontal: COLUMN_GAP / 2,
  },
  flashListContent: {
    paddingHorizontal: COLUMN_GAP / 2,
  },
  contentContainer: {
    paddingHorizontal: CONTENT_PADDING,
  },
  list: {
    flex: 1,
  },
});
